/**
 * Stationery Invitations API
 * API functions for stationery invitation CRUD operations
 *
 * Endpoints match the backend spec:
 * - POST /api/stationery/init
 * - GET /api/stationery/:id
 * - GET /api/stationery
 * - PATCH /api/stationery/:id
 * - DELETE /api/stationery/:id
 * - POST /api/stationery/:id/print-files
 * - POST /api/stationery/:id/ready
 * - POST /api/stationery/:id/duplicate
 * - GET /api/stationery/prefill/:invitationId
 */

import { get, post, patch, del } from '../shared/client'
import type {
  StationeryInvitation,
  StationeryTemplateData,
  StationeryInvitationListItem,
  StationeryListResponse,
  StationeryDetailResponse,
  StationeryInitResponse,
  PaperSize,
} from '@/types/stationery-invitation'

const BASE_URL = '/stationery'

// =============================================================================
// Request Types
// =============================================================================

/** Payload for initializing a new stationery invitation */
export interface InitStationeryPayload {
  /** Optional client-generated UUID */
  id?: string
  /** Optional link to digital invitation */
  invitationId?: string
  /** Paper size (required) */
  paperSize: PaperSize
  /** Initial design selection */
  designId?: string
  /** Initial template data */
  templateData?: Partial<StationeryTemplateData>
  /** Title for reference */
  title?: string
}

/** Payload for updating a stationery invitation */
export interface UpdateStationeryPayload {
  templateData?: Partial<StationeryTemplateData>
  title?: string
}

// =============================================================================
// Core CRUD
// =============================================================================

/**
 * Initialize a new stationery invitation
 * POST /stationery/init
 */
export async function initStationery(
  data: InitStationeryPayload
): Promise<StationeryInvitation> {
  const response = await post<StationeryInitResponse>(`${BASE_URL}/init`, data)
  return response.data
}

/**
 * Get all stationery invitations for the current user
 * GET /api/stationery
 */
export async function getAllStationery(params?: {
  status?: string
  limit?: number
  offset?: number
}): Promise<{ data: StationeryInvitationListItem[]; total: number }> {
  const queryParams = new URLSearchParams()
  if (params?.status) queryParams.set('status', params.status)
  if (params?.limit) queryParams.set('limit', params.limit.toString())
  if (params?.offset) queryParams.set('offset', params.offset.toString())

  const queryString = queryParams.toString()
  const url = queryString ? `${BASE_URL}?${queryString}` : BASE_URL

  const response = await get<StationeryListResponse>(url)
  return { data: response.data, total: response.total }
}

/**
 * Get a single stationery invitation by ID
 * GET /api/stationery/:id
 */
export async function getStationery(id: string): Promise<StationeryInvitation> {
  const response = await get<StationeryDetailResponse>(`${BASE_URL}/${id}`)
  return response.data
}

/**
 * Update an existing stationery invitation
 * PATCH /api/stationery/:id
 *
 * @deprecated Use saveStationery instead for the unified save flow
 */
export async function updateStationery(
  id: string,
  data: UpdateStationeryPayload
): Promise<StationeryInvitation> {
  const response = await patch<StationeryDetailResponse>(
    `${BASE_URL}/${id}`,
    data
  )
  return response.data
}

/**
 * Save stationery invitation (unified endpoint)
 * PATCH /api/stationery/:id/save
 *
 * Supports two modes:
 * 1. Save design only: Pass templateData and title
 * 2. Save with print files: Pass templateData, title, front, and back files
 *
 * When both front and back files are provided, the backend will:
 * - Validate files (format, size, dimensions)
 * - Upload to storage
 * - Update printFiles field
 * - Set status to 'ready'
 *
 * @param id - Stationery ID
 * @param templateData - Design data
 * @param title - Optional title
 * @param front - Optional front page file (PNG/JPEG)
 * @param back - Optional back page file (PNG/JPEG)
 */
/**
 * Save stationery with optional print files (unified endpoint)
 * PATCH /api/stationery/:id/save
 *
 * Combines template data save and print file upload in one request.
 * When both front and back files are provided, status will be set to "ready".
 *
 * @param id - Stationery ID (must exist - call initStationery first for new stationery)
 * @param templateData - Complete template data
 * @param title - Optional title
 * @param front - Optional front page blob/file
 * @param back - Optional back page blob/file
 */
export async function saveStationery(
  id: string,
  templateData: StationeryTemplateData,
  title?: string,
  front?: File | Blob,
  back?: File | Blob
): Promise<StationeryInvitation> {
  const formData = new FormData()

  // Security: Sanitize and validate templateData before sending
  // The backend will also validate, but this prevents unnecessary large payloads
  formData.append('templateData', JSON.stringify(templateData))

  // Always send title (required field)
  if (title?.trim()) {
    formData.append('title', title.trim())
  } else {
    formData.append('title', '')
  }

  // Add print files if provided (both must be present to mark as ready)
  if (front) {
    formData.append('front', front, 'front.jpg')
  }
  if (back) {
    formData.append('back', back, 'back.jpg')
  }

  const response = await patch<StationeryDetailResponse>(
    `${BASE_URL}/${id}/save`,
    formData
  )
  return response.data
}

/**
 * Delete a stationery invitation (soft delete → status: cancelled)
 * DELETE /api/stationery/:id
 */
export async function deleteStationery(id: string): Promise<void> {
  await del(`${BASE_URL}/${id}`)
}

// =============================================================================
// Print Files
// =============================================================================

/**
 * Upload print files for a stationery invitation
 * POST /api/stationery/:id/print-files
 *
 * @param id - Stationery ID
 * @param front - Front page file (PNG/JPEG)
 * @param back - Back page file (PNG/JPEG)
 */
export async function uploadPrintFiles(
  id: string,
  front: File,
  back: File
): Promise<StationeryInvitation> {
  const formData = new FormData()
  formData.append('front', front)
  formData.append('back', back)

  const response = await post<StationeryDetailResponse>(
    `${BASE_URL}/${id}/print-files`,
    formData
  )
  return response.data
}

// =============================================================================
// Status & Actions
// =============================================================================

/**
 * Mark stationery as ready (usually auto-set after print file upload)
 * POST /api/stationery/:id/ready
 */
export async function markStationeryReady(
  id: string
): Promise<StationeryInvitation> {
  const response = await post<StationeryDetailResponse>(
    `${BASE_URL}/${id}/ready`,
    {}
  )
  return response.data
}

/**
 * Duplicate a stationery invitation
 * POST /api/stationery/:id/duplicate
 *
 * Creates a copy in 'draft' status. Print files are NOT copied.
 */
export async function duplicateStationery(
  id: string,
  title?: string
): Promise<StationeryInvitation> {
  const response = await post<StationeryDetailResponse>(
    `${BASE_URL}/${id}/duplicate`,
    { title }
  )
  return response.data
}

// =============================================================================
// Prefill
// =============================================================================

/**
 * Get prefill data from a digital invitation
 * GET /api/stationery/prefill/:invitationId
 */
export async function getPrefillData(invitationId: string): Promise<{
  templateData: Partial<StationeryTemplateData>
  invitationTitle: string
  eventDate: string | null
}> {
  const response = await get<{
    success: boolean
    data: {
      templateData: Partial<StationeryTemplateData>
      invitationTitle: string
      eventDate: string | null
    }
  }>(`${BASE_URL}/prefill/${invitationId}`)
  return response.data
}

// =============================================================================
// Legacy Exports (for backward compatibility)
// =============================================================================

/** @deprecated Use getAllStationery instead */
export const getAllStationeryInvitations = async () => {
  const result = await getAllStationery()
  return result.data
}

/** @deprecated Use getStationery instead */
export const getStationeryInvitation = getStationery

/** @deprecated Use initStationery instead */
export const createStationeryInvitation = async (data: {
  title: string
  templateData: StationeryTemplateData
  invitationId?: string | null
}) => {
  return initStationery({
    title: data.title,
    templateData: data.templateData,
    invitationId: data.invitationId || undefined,
    paperSize: '5x7', // Default for legacy
  })
}

/** @deprecated Use updateStationery instead */
export const updateStationeryInvitation = updateStationery

/** @deprecated Use deleteStationery instead */
export const deleteStationeryInvitation = deleteStationery
